import styled, { keyframes } from "styled-components"

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`

const slideInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(10px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateX(0) scale(1);
  }
`
const slideInLeft = keyframes`
  from {
    transform: translateX(-10px) scaleX(1.1);
  }
  to {
    transform:  translateX(0) scaleX(1);
  }
`

// Add this at the top after the keyframes
const MOBILE_BREAKPOINT = "768px"
const SMALL_MOBILE_BREAKPOINT = "480px"

export const Container = styled.div`
  min-height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  position: relative;
  z-index: 50;
  font-family: 'Poppins', sans-serif;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding: 0.5rem;
    align-items: flex-start;
    min-height: 100vh;
  }
`

// Update the MainCard to have fixed dimensions
export const MainCard = styled.div<{ hasSelectedDate?: boolean }>`
  width: 70rem;
  max-width: 70rem;
  background: ${(props) => props?.theme?.themeConfig?.chatMessages.styles.background ?? "white"};
  border-radius: 0.75rem;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  overflow: visible;
  position: relative;
  z-index: 50;
  animation: ${fadeIn} 0.3s ease-out;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    max-width: 100%;
    border-radius: 0;
    min-height: 100vh;
    box-shadow: none;
  }
`

// Update CardContent to have fixed height
export const CardContent = styled.div`
  display: flex;
  height: 600px;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    flex-direction: column;
    min-height: 100vh;
  }
`

// Update LeftPanel to have fixed height
export const LeftPanel = styled.div`
  border-radius: 0.75rem 0 0 0.75rem;
  width: 40%;
  padding: 2rem;
  background: ${(props) => props?.theme?.themeConfig?.chatMessages.styles.background ?? "white"};
  overflow-y: visible;
  height: 600px;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    width: 100%;
    padding: 1.5rem 1rem;
    border-bottom: 1px solid #e5e7eb;
  }
`

// Update RightPanel to have fixed height
export const RightPanel = styled.div`
  border-radius: 0 0.75rem 0.75rem 0;
  width: 60%;
  padding: 2rem;
  background: ${(props) => props?.theme?.themeConfig?.chatMessages.styles.background ?? "white"};
  border-left: 1px solid #e5e7eb;
  position: relative;
  height: 600px;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    width: 100%;
    padding: 1.5rem 1rem;
    border-left: none;
  }
`

export const CloseButton = styled.button`
  color: ${props => props?.theme?.themeConfig?.chatMessages?.freeform?.title?.styles?.color};
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0.375rem;
  position: absolute;
  top: 1rem;
  right: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    color: ${props => props?.theme?.themeConfig?.chatMessages?.freeform?.title?.styles?.color};
    background: ${props => props?.theme?.themeConfig?.chatMessages?.botMessage?.messageCloud?.styles?.backgroundColor};
  }
`

export const CloseConfirmation = styled.button`
  color: ${props => props?.theme?.themeConfig?.chatMessages?.freeform?.title?.styles?.color};
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0.375rem;
  position: absolute;
  top: 1rem;
  right: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    color: ${(props) => props?.theme?.themeConfig?.primaryColor};
    background: ${props => props?.theme?.themeConfig?.chatMessages?.botMessage?.messageCloud?.styles?.backgroundColor};
  }
`

export const ProfileSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
`

export const Avatar = styled.div`
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  background: ${props => props?.theme?.themeConfig?.chatMessages?.botMessage?.messageCloud?.styles?.backgroundColor};
  display: flex;
  align-items: center;
  justify-content: center;
  
  span {
    color: ${props => props?.theme?.themeConfig?.chatMessages?.freeform?.title?.styles?.color};
    font-weight: 600;
    font-size: 0.875rem;
  }
`

export const ProfileName = styled.p`
  color: ${props => props?.theme?.themeConfig?.chatMessages?.freeform?.title?.styles?.color};
  font-weight: 500;
  margin: 0;
`

export const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${props => props?.theme?.themeConfig?.chatMessages?.freeform?.title?.styles?.color};
  margin: 0 0 0.25rem 0;
  line-height: 1.3;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    font-size: 1.25rem;
  }
`

export const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
`

interface InfoItemProps {
  alignStart?: boolean
}

export const InfoItem = styled.div<InfoItemProps>`
  display: flex;
  align-items: ${(props) => (props.alignStart ? "flex-start" : "center")};
  gap: 0.5rem;
  color: ${props => props?.theme?.themeConfig?.chatMessages?.freeform?.title?.styles?.color};
  
  span {
    font-size: 0.875rem;
    line-height: 1.4;
  }
  
  svg {
    margin-top: ${(props) => (props.alignStart ? "0.125rem" : "0")};
    flex-shrink: 0;
  }
`

export const Description = styled.p`
  color: ${props => props?.theme?.themeConfig?.chatMessages?.freeform?.title?.styles?.color};
  font-size: 0.875rem;
  line-height: 1.6;
  margin: 0;
`

export const SectionTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${props => props?.theme?.themeConfig?.chatMessages?.freeform?.title?.styles?.color};
  margin: 0 0 1.5rem 0;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    font-size: 1.125rem;
    margin: 0 0 1rem 0;
  }
`

export const CalendarAndTimeSlotsContainer = styled.div<{ hasTimeSlots?: boolean }>`
  display: flex;
  gap: 2rem;
  align-items: flex-start;
  transition: gap 0.3s ease;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    flex-direction: column;
    gap: 1.5rem;
  }
`

export const CalendarSection = styled.div<{ hasTimeSlots?: boolean }>`
  flex: ${(props) => (props.hasTimeSlots ? "1" : "none")};
  min-width: ${(props) => (props.hasTimeSlots ? "0" : "450px")};
  width: auto;
  items-align: center;
  justify-content: center;
  margin-left: ${(props) => (props.hasTimeSlots ? "0" : "8rem")};

`

export const TimeSlotsSection = styled.div<{ isVisible: boolean }>`
  width: 196px;
  flex-shrink: 0;
  opacity: ${(props) => (props.isVisible ? 1 : 0.6)};
  transition: all 0.3s ease;
  animation: ${(props) => (props.isVisible ? slideIn : "none")} 0.3s ease-out;
  height: 120%;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    width: 100%;
  }
`

export const TimeSlotsHeader = styled.div`
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #e5e7eb;
`

export const MonthNavigation = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
`

export const NavButton = styled.button`
  padding: 0.5rem;
  border-radius: 0.375rem;
  background: none;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  color: ${props => props?.theme?.themeConfig?.chatMessages?.freeform?.title?.styles?.color};
  &:hover {
    background: ${props => props?.theme?.themeConfig?.chatMessages?.userMessage?.messageCloud?.styles?.backgroundColor};
  }
`

export const MonthTitle = styled.h3`
  font-weight: 600;
  color: ${props => props?.theme?.themeConfig?.chatMessages?.freeform?.title?.styles?.color};
  margin: 0;
  font-size: 1.125rem;
`

export const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0.1rem;
  margin-bottom: 0;
  height: 90%;
`

export const DayHeader = styled.div`
  text-align: center;
  font-size: 0.75rem;
  font-weight: 500;
  color: ${props => props?.theme?.themeConfig?.chatMessages?.freeform?.title?.styles?.color};
  padding: 0.5rem 0;
`

export const CalendarDaysGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0.1rem;
`

export const DayCell = styled.div`
  aspect-ratio: 1;
  padding: 0.125rem;
`

interface DayButtonProps {
  isSelected?: boolean
  isAvailable?: boolean
}

// Update DayButton to be more compact
export const DayButton = styled.button<DayButtonProps>`
  width: 85%;
  height: 85%;
  border-radius: 50%;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  border: none;
  cursor: pointer;
  font-weight: 500;
  
  ${(props) => {
    if (props.isSelected) {
      return `
        background: ${props?.theme?.themeConfig?.primaryColor};
        color: white;
        font-weight: 600;
        transform: scale(1.05);
      `
    } else if (props.isAvailable) {
      return `
        background: ${props?.theme?.themeConfig?.chatMessages?.botMessage?.messageCloud?.styles?.backgroundColor};
        color: ${props?.theme?.themeConfig?.chatMessages?.freeform?.title?.styles?.color};
        
        &:hover {
          background: ${props?.theme?.themeConfig?.primaryColor};
          color: white;
          transform: scale(1.02);
        }
      `
    } else {
      return `
        color: ${props?.theme?.themeConfig?.chatMessages?.freeform?.title?.styles?.color};
        cursor: not-allowed;
        background: transparent;
        opacity: 0.3;
      `
    }
  }}
`

export const TimeSlotsTitle = styled.h4`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${props => props?.theme?.themeConfig?.chatMessages?.freeform?.title?.styles?.color};
  margin: 0;
`

// Update TimeSlotsGrid to have better spacing
export const TimeSlotsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 400px;
  overflow-y: auto;
  padding-right: 0.5rem;
  padding-top: 0.5rem;
  width: 100%;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${props => props?.theme?.themeConfig?.chatMessages?.botMessage?.messageCloud?.styles?.backgroundColor};
    border-radius: 2px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${props => props?.theme?.themeConfig?.chatMessages?.freeform?.title?.styles?.color};
    opacity: 0.3;
    border-radius: 2px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: ${props => props?.theme?.themeConfig?.chatMessages?.freeform?.title?.styles?.color};
    opacity: 0.5;
  }
`

export const TimeSlotRow = styled.div`
  display: flex;
  width: 100%;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  align-items: center;
  color: ${props => props?.theme?.themeConfig?.chatMessages?.freeform?.title?.styles?.color};
`

interface TimeSlotButtonProps {
  isSelected?: boolean
}

export const TimeSlotButton = styled.button<TimeSlotButtonProps>`
  padding: 0.75rem 0.5rem;
  font-size: 0.875rem;
  border-radius: 0.375rem;
  border: 1px solid;
  transition: all 0.2s ease;
  cursor: pointer;
  text-align: center;
  font-weight: 500;
  flex: 1;
  animation: ${slideInLeft} 0.3s ease-out;
  color: ${props => props?.theme?.themeConfig?.chatMessages?.freeform?.title?.styles?.color};

  ${(props) =>
    props.isSelected
      ? `
    background: ${props?.theme?.themeConfig?.chatMessages?.botMessage?.messageCloud?.styles?.backgroundColor};
  color: ${props?.theme?.themeConfig?.chatMessages?.freeform?.title?.styles?.color};
  border-color: ${props?.theme?.themeConfig?.primaryColor};
`
      : `
  background: ${props?.theme?.themeConfig?.chatMessages.styles.background ?? "white"};
  color: ${props?.theme?.themeConfig?.chatMessages?.freeform?.title?.styles?.color};
  border-color: ${props?.theme?.themeConfig?.chatMessages?.freeform?.title?.styles?.color};
  
  &:hover {
    border-color: ${props?.theme?.themeConfig?.primaryColor};
    background: ${props?.theme?.themeConfig?.chatMessages?.botMessage?.messageCloud?.styles?.backgroundColor};
  }
`}
`

export const TimeZoneSection = styled.div`
  margin-top: 0.5rem;
`

export const TimeZoneLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${props => props?.theme?.themeConfig?.chatMessages?.freeform?.title?.styles?.color};
  display: block;
  margin-bottom: 0.5rem;
`

export const TimeZoneDropdown = styled.div`
  position: relative;
  color: ${props => props?.theme?.themeConfig?.chatMessages?.freeform?.title?.styles?.color};
`

export const TimeZoneButton = styled.button`
  width: 100%;
  padding: 0.75rem;
  border: ${(props) => props?.theme?.themeConfig?.chatMessages?.botMessage?.messageCloud?.styles?.backgroundColor} solid 1px;
  border-radius: 0.375rem;
  text-align: left;
  font-size: 0.875rem;
  background: ${props => props?.theme?.themeConfig?.chatMessages?.botMessage?.messageCloud?.styles?.backgroundColor};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: ${props => props?.theme?.themeConfig?.chatMessages?.freeform?.title?.styles?.color};
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }

`

export const TimeZoneSearchContainer = styled.div`
  padding: 1rem;
`

export const TimeZoneSearchInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: ${(props) => props?.theme?.themeConfig?.primaryColor} solid 1px;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  background: ${props => props?.theme?.themeConfig?.chatMessages?.botMessage?.messageCloud?.styles?.backgroundColor};
  color: ${props => props?.theme?.themeConfig?.chatMessages?.freeform?.title?.styles?.color};
  &:focus {
    outline: none;
    border-color: ${(props) => props?.theme?.themeConfig?.primaryColor};
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }
  
  &::placeholder {
  color: ${props => props?.theme?.themeConfig?.chatMessages?.freeform?.title?.styles?.color};
  }
`

export const TimeZoneHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  background: ${props => props?.theme?.themeConfig?.chatMessages?.botMessage?.messageCloud?.styles?.backgroundColor};
`

export const TimeZoneHeaderTitle = styled.h3`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${props => props?.theme?.themeConfig?.chatMessages?.freeform?.title?.styles?.color};
  margin: 0;
`

export const TimeFormatToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: ${props => props?.theme?.themeConfig?.chatMessages?.botMessage?.messageCloud?.styles?.backgroundColor};
  border-radius: 1rem;
  padding: 0.25rem;
`

export const TimeFormatButton = styled.button<{ active?: boolean }>`
  padding: 0.25rem 0.75rem;
  border-radius: 0.75rem;
  font-size: 0.75rem;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${(props) =>
    props.active
      ? `
    background: ${props?.theme?.themeConfig?.chatMessages?.botMessage?.messageCloud?.styles?.backgroundColor};
    color: #374151;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  `
      : `
  color: ${props?.theme?.themeConfig?.chatMessages?.freeform?.title?.styles?.color};
    
    &:hover {
      color: #374151;
    }
  `}
`

export const TimeZoneGroupHeader = styled.div`
  padding: 0.75rem 1rem 0.5rem 1rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${props => props?.theme?.themeConfig?.chatMessages?.freeform?.title?.styles?.color};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: ${props => props?.theme?.themeConfig?.chatMessages?.botMessage?.messageCloud?.styles?.backgroundColor};
`

export const TimeZoneOptionWithTime = styled.div<{ selected?: boolean }>`
  cursor: pointer;
  padding: 0.75rem 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: background 0.15s ease;
  
  ${(props) =>
    props.selected
      ? `
    background: ${props?.theme?.themeConfig?.primaryColor};
    color: ${props?.theme?.themeConfig?.chatMessages?.freeform?.title?.styles?.color};
  `
      : `
    &:hover {
    background: ${props?.theme?.themeConfig?.chatMessages?.userMessage?.messageCloud?.styles?.backgroundColor};
    }
  `}
`

export const TimeZoneOptionLabel = styled.div`
  font-size: 0.875rem;
`

export const TimeZoneOptionTime = styled.div`
  font-size: 0.75rem;
  opacity: 0.8;
  font-weight: 500;
`

export const SelectedTimezoneDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${props => props?.theme?.themeConfig?.chatMessages?.freeform?.title?.styles?.color};
  
  svg {
    width: 16px;
    height: 16px;
    opacity: 0.7;
  }
`

export const TimeZoneOptions = styled.div`
  position: absolute;
  z-index: 50;
  margin-top: 0.25rem;
  width: 100%;
  background: ${props => props?.theme?.themeConfig?.chatMessages.styles.background ?? "white"};
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  max-height: 300px;
  border-radius: 0.5rem;
  border: 1px solid ${(props)=>props?.theme?.themeConfig?.primaryColor};
  overflow: hidden;
  animation: ${fadeIn} 0.2s ease-out;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 90vw;
    max-width: 400px;
    max-height: 70vh;
  }
`

export const TimeZoneScrollableContent = styled.div`
  max-height: 300px;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${props => props?.theme?.themeConfig?.chatMessages?.botMessage?.messageCloud?.styles?.backgroundColor};
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${props => props?.theme?.themeConfig?.chatMessages?.freeform?.title?.styles?.color};
    opacity: 0.3;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: ${props => props?.theme?.themeConfig?.chatMessages?.freeform?.title?.styles?.color};
    opacity: 0.5;
  }
`

export const TimeZoneOption = styled.div`
  cursor: pointer;
  padding: 0.75rem;
  transition: background 0.15s ease;
  
  &:hover {
    background: ${props => props?.theme?.themeConfig?.chatMessages?.userMessage?.messageCloud?.styles?.backgroundColor};
  }
`

export const ConfirmButton = styled.button<{ disabled?: boolean }>`
  position: absolute;
  bottom: 2rem;
  right: 2rem;
  color:${(props) => props?.theme?.themeConfig?.primaryColor};
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0.375rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: #f3f4f6;
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
`

export const LoadingSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 2rem 1rem;
  color: ${props => props?.theme?.themeConfig?.chatMessages?.freeform?.title?.styles?.color};
  opacity: 0.6;
  text-align: center;
  
  span {
    font-size: 0.875rem;
  }
  
  svg {
    opacity: 0.5;
  }
`

// Booking Details Form Styles
export const BookingDetailsContainer = styled.div`
  min-height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  font-family: 'Poppins', sans-serif;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding: 0;
    align-items: flex-start;
  }
`

export const BookingDetailsCard = styled.div`
  width: 100%;
  max-width: 70rem;
  background: ${props => props?.theme?.themeConfig?.chatMessages.styles.background ?? "white"};
  border-radius: 0.75rem;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  overflow: hidden;
  position: relative;
  animation: ${fadeIn} 0.3s ease-out;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    max-width: 100%;
    border-radius: 0;
    min-height: 100vh;
    box-shadow: none;
  }
`

export const BookingDetailsContent = styled.div`
  display: flex;
  min-height: 600px;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    flex-direction: column;
    min-height: 100vh;
  }
`

export const BookingDetailsLeft = styled.div`
  width: 40%;
  padding: 2rem;
  background: ${props => props?.theme?.themeConfig?.chatMessages.styles.background ?? "white"};
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    width: 100%;
    padding: 1.5rem 1rem;
    border-bottom: 1px solid #e5e7eb;
  }
`

export const BookingDetailsRight = styled.div`
  width: 60%;
  padding: 2rem;
  background: ${props => props?.theme?.themeConfig?.chatMessages.styles.background ?? "white"};
  border-left: 1px solid #e5e7eb;
  position: relative;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    width: 100%;
    padding: 1.5rem 1rem;
    border-left: none;
  }
`

export const BackButton = styled.button`
  position: absolute;
  bottom: 2rem;
  left: 2rem;
  color:${(props) => props?.theme?.themeConfig?.primaryColor};
  background: none;
  border: 1px solid;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0.375rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: ${ props => props?.theme?.themeConfig?.chatMessages?.botMessage?.messageCloud?.styles?.backgroundColor};
    border-color: ${(props) => props?.theme?.themeConfig?.primaryColor};
  }
`

export const BookingDetailsHeader = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${props => props?.theme?.themeConfig?.chatMessages?.freeform?.title?.styles?.color};
  margin: 0 0 2rem 0;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    font-size: 1.25rem;
    margin: 0 0 1.5rem 0;
  }
`

export const FormSection = styled.div`
  margin-bottom: 1.5rem;
  color: ${props => props?.theme?.themeConfig?.chatMessages?.freeform?.title?.styles?.color};
`

export const FormLabel = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: ${props => props?.theme?.themeConfig?.chatMessages?.freeform?.title?.styles?.color};
  margin-bottom: 0.5rem;
`

// Add these new styled components for inline error messages
export const FormInputContainer = styled.div`
  position: relative;
  width: 100%;
`

export const InlineErrorIcon = styled.div`
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: ${(props)=>props?.theme?.themeConfig?.primaryColor};
  display: flex;
  align-items: center;
  z-index: 2;
`

export const FormInput = styled.input<{ hasError?: boolean }>`
  width: 100%;
  padding: 0.75rem;
  padding-right: ${(props) => (props.hasError ? "2.5rem" : "0.75rem")};
  border: 1px solid #808080;
  background: ${props => props?.theme?.themeConfig?.chatMessages.styles.background ?? "white"};
  transition: all 0.2s ease;
  color: ${props => props?.theme?.themeConfig?.chatMessages?.freeform?.title?.styles?.color};
  border-radius: 0.375rem;
  
  &:focus {
    outline: none;
    border-color: ${(props) => (props.hasError ? props?.theme?.themeConfig?.primaryColor : props?.theme?.themeConfig?.primaryColor)};
    box-shadow: ${(props) =>
      props.hasError ? `0 0 0 3px ${props?.theme?.themeConfig?.primaryColor}33` : `0 0 0 3px ${props?.theme?.themeConfig?.primaryColor}33`};
  }
  
  &::placeholder {
    color: ${props => props?.theme?.themeConfig?.chatMessages?.freeform?.title?.styles?.color};
    opacity: 0.5;
  }
`

export const FormTextarea = styled.textarea<{ hasError?: boolean }>`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #808080;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  background: ${props => props?.theme?.themeConfig?.chatMessages.styles.background ?? "white"};
  resize: vertical;
  min-height: 100px;
  font-family: inherit;
  transition: all 0.2s ease;
  color: ${props => props?.theme?.themeConfig?.chatMessages?.freeform?.title?.styles?.color};

  
  &:focus {
    outline: none;
    border-color: ${(props) => (props.hasError ? props?.theme?.themeConfig?.primaryColor : props?.theme?.themeConfig?.primaryColor)};
    box-shadow: ${(props) =>
      props.hasError ? `0 0 0 3px ${props?.theme?.themeConfig?.primaryColor}33` : `0 0 0 3px ${props?.theme?.themeConfig?.primaryColor}33`};
    color: ${props => props?.theme?.themeConfig?.chatMessages?.freeform?.title?.styles?.color};
  }
  
  &::placeholder {
    color: ${props => props?.theme?.themeConfig?.chatMessages?.freeform?.title?.styles?.color};
    opacity: 0.5;
  }
`

export const ErrorTooltip = styled.div`
  position: absolute;
  top: -0.5rem;
  right: 0.5rem;
  transform: translateY(-100%);
  background-color: ${(props)=>props?.theme?.themeConfig?.primaryColor};
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  white-space: nowrap;
  z-index: 10;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.2s ease, visibility 0.2s ease;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -0.25rem;
    right: 0.5rem;
    width: 0.5rem;
    height: 0.5rem;
    background-color: ${(props)=>props?.theme?.themeConfig?.primaryColor};
    transform: rotate(45deg);
  }
  
  ${InlineErrorIcon}:hover + & {
    opacity: 1;
    visibility: visible;
  }
`

export const AddGuestsButton = styled.button`
  padding: 0.75rem 1rem;
  border: 1px solid ${(props) => props?.theme?.themeConfig?.primaryColor};
  border-radius: 0.375rem;
  background: ${props => props?.theme?.themeConfig?.chatMessages.styles.background ?? "white"};
  color: ${(props) => props?.theme?.themeConfig?.primaryColor};
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 1.5rem;
  
  &:hover {
    background: ${props => props?.theme?.themeConfig?.chatMessages?.userMessage?.messageCloud?.styles?.backgroundColor};
  }
`

export const TermsText = styled.p`
  font-size: 0.75rem;
  color: ${props => props?.theme?.themeConfig?.chatMessages?.freeform?.title?.styles?.color};
  opacity: 0.7;
  line-height: 1.5;
  margin: 1.5rem 0;
`

export const TermsLink = styled.a`
  color: ${(props) => props?.theme?.themeConfig?.primaryColor};
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`

export const ScheduleButton = styled.button<{ disabled?: boolean }>`
  position: relative;
  width: 40%;
  background: ${(props) => props?.theme?.themeConfig?.primaryColor};
  color: white;
  padding: 1rem;
  border-radius: 0.5rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  margin-top: 1.5rem;
  
  &:hover:not(:disabled) {
    background: ${(props) => {
      const primary = props?.theme?.themeConfig?.primaryColor
      try {
        const darken = (hex: string, amt = -25) => {
          let col = hex.replace("#", "")
          if (col.length === 3)
            col = col
              .split("")
              .map((c) => c + c)
              .join("")
          const num = Number.parseInt(col, 16)
          let r = (num >> 16) + amt
          let g = ((num >> 8) & 0x00ff) + amt
          let b = (num & 0x0000ff) + amt
          r = Math.max(Math.min(255, r), 0)
          g = Math.max(Math.min(255, g), 0)
          b = Math.max(Math.min(255, b), 0)
          return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`
        }
        return darken(primary, -25)
      } catch {
        return primary
      }
    }};  
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`

export const MeetingDetailsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

export const MeetingDetailItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  color: ${props => props?.theme?.themeConfig?.chatMessages?.freeform?.title?.styles?.color};
  
  span {
    font-size: 0.875rem;
    line-height: 1.4;
  }
  
  svg {
    margin-top: 0.125rem;
    flex-shrink: 0;
  }
`

export const FooterLinks = styled.div`
  display: flex;
  gap: 2rem;
  margin-top: 2rem;
`

export const FooterLink = styled.button`
  color: ${(props) => props?.theme?.themeConfig?.primaryColor};
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.875rem;
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`

// Guest Email Components
export const GuestEmailContainer = styled.div`
  border: none;
  padding: 0rem;
  min-height: 0rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  background: ${props => props?.theme?.themeConfig?.chatMessages.styles.background ?? "white"};
  transition: all 0.2s ease;
`

export const GuestEmailTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`

export const GuestEmailTag = styled.div`
  display: flex;
  align-items: center;
  background: ${props => props?.theme?.themeConfig?.chatMessages?.botMessage?.messageCloud?.styles?.backgroundColor};
  border-radius: 0.25rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
  gap: 0.5rem;
`

export const GuestEmailTagText = styled.span`
  color: ${props => props?.theme?.themeConfig?.chatMessages?.freeform?.title?.styles?.color};
`

export const RemoveGuestButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${props => props?.theme?.themeConfig?.chatMessages?.freeform?.title?.styles?.color};
  opacity: 0.6;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.125rem;
  border-radius: 0.125rem;
  transition: all 0.2s ease;
  
  &:hover {
    color: ${props => props?.theme?.themeConfig?.chatMessages?.freeform?.title?.styles?.color};
    background: ${props => props?.theme?.themeConfig?.chatMessages?.userMessage?.messageCloud?.styles?.backgroundColor};
  }
`

export const GuestEmailInput = styled.input`
  border: none;
  outline: none;
  padding: 0.25rem;
  font-size: 0.875rem;
  background: transparent;
  flex: 1;
  min-width: 200px;
  
  &::placeholder {
    color: ${props => props?.theme?.themeConfig?.chatMessages?.freeform?.title?.styles?.color};
    opacity: 0.5;
  }
`

// Confirmation Modal Styles
export const ConfirmationContainer = styled.div`
  min-height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  font-family: 'Poppins', sans-serif;
`

export const ConfirmationCard = styled.div`
  max-width: 28rem;
  background: ${props => props?.theme?.themeConfig?.chatMessages.styles.background ?? "white"};
  border-radius: 0.75rem;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  padding: 2rem;
  text-align: center;
  position: relative;
  animation: ${fadeIn} 0.3s ease-out;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    max-width: 90vw;
    padding: 1.5rem;
    margin: 1rem;
  }
`

export const ConfirmationIcon = styled.div`
  margin-bottom: 1.5rem;
  
  svg {
    height: 4rem;
    width: 4rem;
    color: #10b981;
    margin: 0 auto 1rem auto;
  }
`

export const ConfirmationTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${props => props?.theme?.themeConfig?.chatMessages?.freeform?.title?.styles?.color};
  margin: 0 0 0.5rem 0;
`

export const ConfirmationSubtitle = styled.p`
  color: ${props => props?.theme?.themeConfig?.chatMessages?.freeform?.title?.styles?.color};
  opacity: 0.7;
  margin: 0;
`

export const ConfirmationDetails = styled.div`
  background: ${props => props?.theme?.themeConfig?.chatMessages?.botMessage?.messageCloud?.styles?.backgroundColor};
  border-radius: 0.5rem;
  padding: 1.5rem;
  margin: 1.5rem 0;
  text-align: left;
`

export const ConfirmationDetailsTitle = styled.h3`
  font-weight: 600;
  color: ${props => props?.theme?.themeConfig?.chatMessages?.freeform?.title?.styles?.color};
  margin: 0 0 1rem 0;
`

export const ConfirmationDetailsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  font-size: 0.875rem;
  color: ${props => props?.theme?.themeConfig?.chatMessages?.freeform?.title?.styles?.color};
  opacity: 0.8;
`

export const ConfirmationDetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  
  svg {
    flex-shrink: 0;
  }
`

// Add these new responsive utility classes
export const ResponsiveContainer = styled.div`
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    .calendar_container {
      padding: 0.5rem;
    }
    
    .main_card {
      border-radius: 0;
      box-shadow: none;
    }
    
    .left_panel_content {
      gap: 1rem !important;
    }
    
    .right_panel_content {
      gap: 1rem !important;
    }
    
    .profile_section {
      margin-bottom: 1rem !important;
    }
    
    .meeting_title {
      font-size: 1.25rem !important;
    }
    
    .section_title {
      font-size: 1.125rem !important;
      margin-bottom: 1rem !important;
    }
    
    .calendar_section {
      width: 100% !important;
    }
    
    .timeslots_section {
      width: 100% !important;
    }
    
    .day_button {
      min-height: 44px;
      min-width: 44px;
    }
    
    .timeslot_button {
      min-height: 44px;
      padding: 0.875rem 1rem;
    }
    
    .nav_button {
      min-height: 44px;
      min-width: 44px;
    }
    
    .close_button {
      min-height: 44px;
      min-width: 44px;
    }
    
    .timezone_button {
      min-height: 44px;
    }
    
    .confirm_button {
      min-height: 48px;
      font-size: 1rem;
    }
  }
  
  @media (max-width: ${SMALL_MOBILE_BREAKPOINT}) {
    .calendar_grid {
      gap: 0.125rem;
    }
    
    .calendar_days_grid {
      gap: 0.125rem;
    }
    
    .day_button {
      font-size: 0.75rem;
    }
    
    .month_title {
      font-size: 1rem;
    }
  }
`

// Mobile specific components
export const MobileContainer = styled.div`
  min-height: 100vh;
  background: ${props => props?.theme?.themeConfig?.chatMessages.styles.background ?? "white"};
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  font-family: 'Poppins', sans-serif;
`

export const MobileHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  border-bottom: 1px solid ${(props)=>props?.theme?.themeConfig?.chatMessages?.freeform?.title?.styles?.color};
  opacity: 0.2;
  position: relative;
  background: ${props => props?.theme?.themeConfig?.chatMessages.styles.background ?? "white"};
  z-index: 10;
`

export const MobileBackButton = styled.button`
  position: absolute;
  left: 1rem;
  color: ${(props) => props?.theme?.themeConfig?.primaryColor};
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  min-height: 44px;
  min-width: 44px;

  &:hover {
    background: ${props => props?.theme?.themeConfig?.chatMessages?.userMessage?.messageCloud?.styles?.backgroundColor};
  }
`

export const MobileTitle = styled.h1`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${props => props?.theme?.themeConfig?.chatMessages?.freeform?.title?.styles?.color};
  margin: 0;
  text-align: center;
`

export const MobileContent = styled.div`
  flex: 1;
  padding: 1.5rem 1rem;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`

export const MobileFooter = styled.div`
  padding: 1rem;
  border-top: 1px solid ${(props)=>props?.theme?.themeConfig?.chatMessages?.freeform?.title?.styles?.color};
  opacity: 0.2;
  background: ${props => props?.theme?.themeConfig?.chatMessages.styles.background ?? "white"};
`

export const MobileNextButton = styled.button`
  width: 100%;
  background: ${(props) => props?.theme?.themeConfig?.primaryColor};
  color: white;
  padding: 1rem;
  border-radius: 0.5rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 1rem;
  min-height: 48px;
  
  &:hover:not(:disabled) {
    background: ${(props) => {
      const primary = props?.theme?.themeConfig?.primaryColor
      try {
        const darken = (hex: string, amt = -25) => {
          let col = hex.replace("#", "")
          if (col.length === 3)
            col = col
              .split("")
              .map((c) => c + c)
              .join("")
          const num = Number.parseInt(col, 16)
          let r = (num >> 16) + amt
          let g = ((num >> 8) & 0x00ff) + amt
          let b = (num & 0x0000ff) + amt
          r = Math.max(Math.min(255, r), 0)
          g = Math.max(Math.min(255, g), 0)
          b = Math.max(Math.min(255, b), 0)
          return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`
        }
        return darken(primary, -25)
      } catch {
        return primary
      }
    }};
}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

export const MobileStepIndicator = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  padding: 1rem;
`

export const MobileStep = styled.div<{ active?: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(props) => (props.active ? props?.theme?.themeConfig?.primaryColor : "")};
  transition: all 0.2s ease;
`

export const NextButton = styled.button`
  background: ${(props) => props?.theme?.themeConfig?.primaryColor};
  color: white;
  border: none;
  border-radius: 0.375rem;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  cursor: pointer;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  animation: ${slideInRight} 0.3s ease-out;
  white-space: nowrap;
  
  &:hover {
background: ${(props) => {
  const primary = props?.theme?.themeConfig?.primaryColor
  try {
    const darken = (hex: string, amt = -25) => {
      let col = hex.replace("#", "")
      if (col.length === 3)
        col = col
          .split("")
          .map((c) => c + c)
          .join("")
      const num = Number.parseInt(col, 16)
      let r = (num >> 16) + amt
      let g = ((num >> 8) & 0x00ff) + amt
      let b = (num & 0x0000ff) + amt
      r = Math.max(Math.min(255, r), 0)
      g = Math.max(Math.min(255, g), 0)
      b = Math.max(Math.min(255, b), 0)
      return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`
    }
    return darken(primary, -25)
  } catch {
    return primary
  }
}};      box-shadow: 0 4px 8px rgba(59, 130, 246, 0.3);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding: 0.875rem 1.25rem;
    font-size: 0.875rem;
  }
`